import { useVerifiedCustomerStore } from "@expert/sales-tools";
import { Button, Divider, Group, Stack, Text, TextInput } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

const PRESET_SHIPPED = {
    imei: "490154203237518",
    make: "Apple",
    model: "iPhone 15 Pro",
    claimDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    serviceOrderStatus: "SHIPPED",
};

const PRESET_PROCESSING = {
    imei: "356938035643809",
    make: "Samsung",
    model: "Galaxy S24 Ultra",
    claimDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    serviceOrderStatus: "PROCESSING",
};

const EMIT_SUCCESS_DURATION_MS = 2000;

export function ClaimTab({ sessionId }: { sessionId: string }) {
    const [imei, setImei] = useState(PRESET_SHIPPED.imei);
    const [make, setMake] = useState(PRESET_SHIPPED.make);
    const [model, setModel] = useState(PRESET_SHIPPED.model);
    const [claimDate, setClaimDate] = useState(PRESET_SHIPPED.claimDate);
    const [status, setStatus] = useState(PRESET_SHIPPED.serviceOrderStatus);
    const [feedback, setFeedback] = useState<"injected" | "cleared" | null>(null);

    useEffect(() => {
        if (!feedback) return;
        const id = setTimeout(() => setFeedback(null), EMIT_SUCCESS_DURATION_MS);
        return () => clearTimeout(id);
    }, [feedback]);

    const applyPreset = (preset: typeof PRESET_SHIPPED) => {
        setImei(preset.imei);
        setMake(preset.make);
        setModel(preset.model);
        setClaimDate(preset.claimDate);
        setStatus(preset.serviceOrderStatus);
    };

    const inject = useCallback(() => {
        if (!imei) return;
        useVerifiedCustomerStore.getState().setDeviceReplacement(sessionId, {
            imei,
            make,
            model,
            eSim: false,
            claimDate: claimDate || undefined,
            serviceOrderStatus: status || undefined,
        });
        setFeedback("injected");
    }, [sessionId, imei, make, model, claimDate, status]);

    const reset = useCallback(() => {
        const store = useVerifiedCustomerStore.getState();
        const current = store.getVerifiedCustomer(sessionId);
        if (current.deviceReplacement) {
            store.setDeviceReplacement(sessionId, undefined);
        }
        setImei("");
        setMake("");
        setModel("");
        setClaimDate("");
        setStatus("");
        setFeedback("cleared");
    }, [sessionId]);

    return (
        <Stack gap="sm">
            <Divider label="Presets" labelPosition="left" />

            <Group gap="xs">
                <Button size="xs" variant="light" color="violet" onClick={() => applyPreset(PRESET_SHIPPED)}>
                    Shipped
                </Button>
                <Button size="xs" variant="light" color="violet" onClick={() => applyPreset(PRESET_PROCESSING)}>
                    Processing
                </Button>
            </Group>

            <Divider label="Claim Fields" labelPosition="left" />

            <TextInput
                label="IMEI"
                placeholder="e.g. 490154203237518"
                value={imei}
                onChange={(e) => setImei(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Make"
                placeholder="e.g. Apple"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Model"
                placeholder="e.g. iPhone 15 Pro"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Claim Date (ISO)"
                placeholder="e.g. 2026-10-01T12:00:00Z"
                value={claimDate}
                onChange={(e) => setClaimDate(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Service Order Status"
                placeholder="e.g. SHIPPED"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                size="xs"
            />

            <Group gap="xs" align="center">
                <Button size="xs" variant="filled" color="violet" onClick={inject} disabled={!imei}>
                    Inject Claim
                </Button>
                <Button size="xs" variant="light" color="red.5" onClick={reset}>
                    Reset
                </Button>
                {feedback && (
                    <Text size="xs" c="green.3" fw={500}>
                        {feedback === "injected" ? "Injected" : "Cleared"}
                    </Text>
                )}
            </Group>
        </Stack>
    );
}
